import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Eye,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, token, loading: authLoading } = useAuth();
  
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    
    if (!token) {
      navigate('/login', { state: { from: { pathname: '/dashboard' } } });
      return;
    }
    fetchMyBusinesses();
  }, [token, authLoading, navigate]);

  const fetchMyBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/my-businesses`);
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (businessId) => {
    try {
      await axios.delete(`${API}/businesses/${businessId}`);
      toast.success(language === 'ta' ? 'கடை நீக்கப்பட்டது' : 'Shop deleted');
      fetchMyBusinesses();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: 'outline', icon: Clock, label: t('pending'), className: 'text-yellow-600 border-yellow-600' },
      approved: { variant: 'default', icon: CheckCircle, label: t('approved'), className: 'bg-green-600' },
      rejected: { variant: 'destructive', icon: XCircle, label: t('rejected'), className: '' }
    };
    const { variant, icon: Icon, label, className } = config[status] || config.pending;
    return (
      <Badge variant={variant} className={className}>
        <Icon className="h-3 w-3 mr-1" />
        {label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-8">
        <div className="container mx-auto px-4 md:px-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/20 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('home')}
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white font-['Manrope']">
                {t('dashboard')}
              </h1>
              <p className="text-blue-100 mt-1">
                {language === 'ta' 
                  ? `வணக்கம், ${user?.name}`
                  : `Welcome, ${user?.name}`
                }
              </p>
            </div>
            <Button
              onClick={() => navigate('/add-shop')}
              className="bg-[#E85A24] hover:bg-[#d14918]"
              data-testid="add-new-shop-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('addYourShop')}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-6">{t('myShops')}</h2>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-xl h-32" />
            ))}
          </div>
        ) : businesses.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-slate-500 mb-4">
                {language === 'ta' 
                  ? 'உங்களிடம் இன்னும் கடைகள் இல்லை'
                  : 'You don\'t have any shops yet'
                }
              </p>
              <Button
                onClick={() => navigate('/add-shop')}
                className="bg-[#E85A24] hover:bg-[#d14918]"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('addYourShop')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {businesses.map((business) => (
              <Card key={business.id} data-testid={`shop-card-${business.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Image */}
                    <div className="w-full md:w-32 h-32 flex-shrink-0">
                      <img
                        src={business.images?.[0] || 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=200'}
                        alt={business.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 font-['Manrope']">
                            {language === 'ta' && business.name_ta ? business.name_ta : business.name}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {language === 'ta' ? business.category_name_ta : business.category_name_en}
                          </p>
                        </div>
                        {getStatusBadge(business.status)}
                      </div>
                      
                      <p className="text-slate-600 text-sm mt-2 line-clamp-2">
                        {language === 'ta' && business.description_ta ? business.description_ta : business.description}
                      </p>

                      <div className="flex items-center gap-4 mt-4">
                        <span className="text-sm text-slate-500">
                          ⭐ {business.avg_rating} ({business.review_count} {t('reviews')})
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex md:flex-col gap-2">
                      {business.status === 'approved' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/business/${business.id}`)}
                          data-testid={`view-btn-${business.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {language === 'ta' ? 'காண்க' : 'View'}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/edit-shop/${business.id}`)}
                        data-testid={`edit-btn-${business.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        {t('edit')}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-btn-${business.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {t('delete')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {language === 'ta' ? 'நீக்க உறுதிப்படுத்தவும்' : 'Confirm Delete'}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {language === 'ta' 
                                ? 'இந்த கடையை நிரந்தரமாக நீக்க விரும்புகிறீர்களா?'
                                : 'Are you sure you want to permanently delete this shop?'
                              }
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{language === 'ta' ? 'ரத்துசெய்' : 'Cancel'}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(business.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {t('delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
